import classnames from 'classnames'

import type { PLTypedWidgetProps } from '@src/components/pl-base-widget'
import Swiper from '@src/quince-components/src/atoms/swiper'
import LAYOUT_TYPES from '@src/quince-site-common/src/constants/layoutTypes'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'

import { resolveSectionBackground } from '../widget-background'
import { WidgetMedia, WidgetRichText } from '../widget-content'

import styles from './trust-badges.module.scss'

import type { TrustBadgesWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

/**
 * TRUST_BADGES widget (non-DS / legacy path).
 *
 * A warm band of trust badges, each stacking a centered line icon, an uppercase title, and a short
 * supporting line. Styling lives in `trust-badges.module.scss`. Self-contained; data comes from
 * `data.trustBadgesData`.
 *
 * On mobile the non-PL source section (`TEXT_BLOCK_WITH_ICON_ROW` → `FeatureSection`) renders a
 * one-badge-per-slide Swiper when the section's `layoutType` is CAROUSEL. The proto doesn't carry
 * `layoutType` yet (pending BE contract, like `classNames`), so it's read via cast and missing
 * values default to CAROUSEL — every known TRUST_BADGES page today is a carousel section, and the
 * cast keeps us honoring an explicit DEFAULT the moment the BE starts sending it.
 */
export function TrustBadgesWidgetLegacy(props: PLTypedWidgetProps<TrustBadgesWidgetData>) {
  const deviceInfo = useDeviceInfo()
  const data = props.data.trustBadgesData
  const items = data?.items ?? []
  if (items.length === 0) return null

  // CMS-authored section styling. `classNames` are applied raw (resolved by global CSS, like the
  // non-PL `useDynamicStyle` flow) and known background tokens also resolve to an inline color.
  // NOTE: cast until the proto carries `classNames` (pending BE contract); remove once it lands.
  const classNames = (data as { classNames?: string[] } | undefined)?.classNames
  const backgroundColor = resolveSectionBackground(classNames)

  const layoutType = (data as { layoutType?: string } | undefined)?.layoutType
  const isMobile = deviceInfo.deviceType === DEVICE_TYPES.MOBILE
  const isCarousel = isMobile && layoutType !== LAYOUT_TYPES.DEFAULT

  const renderBadge = (item: (typeof items)[number], index: number) => (
    // Titles aren't guaranteed unique/non-empty → suffix the index for a stable key.
    <div key={`${item.title || 'trust-badge'}-${index}`} className={styles.badge}>
      <WidgetMedia media={item.icon} className={styles.icon} />
      {item.title ? <span className={styles.title}>{item.title}</span> : null}
      <WidgetRichText data={item.subTitle} className={styles.subtitle} />
    </div>
  )

  return (
    <section
      className={classnames(isCarousel ? styles.carouselSection : styles.section, classNames)}
      style={backgroundColor ? { backgroundColor } : undefined}
      data-testid="trust-badges-widget"
    >
      {data?.heading ? <h2 className={styles.heading}>{data.heading}</h2> : null}
      {isCarousel ? (
        <Swiper
          // @i18n-ignore
          id="trust-badges-carousel"
          showDots
          showNavigator={false}
          numberOfVisibleSlides={1}
          variableWidth
          freeMode={false}
          initialSlide={0}
          virtualOptions={{ enabled: true }}
          className={styles.cardList}
          slideClassName={styles.cardListSlide}
        >
          {items.map((item, index) => (
            <div key={`${item.title || 'trust-badge'}-${index}`} className={styles.sliderWrapper}>
              {renderBadge(item, index)}
            </div>
          ))}
        </Swiper>
      ) : (
        <div className={styles.row}>{items.map(renderBadge)}</div>
      )}
    </section>
  )
}
